import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { RouterProvider } from 'react-router'
import { QueryClient, QueryClientProvider, MutationCache, QueryCache } from '@tanstack/react-query'
import { router } from './routes'
import { initSentry, captureHandledError } from './sentry'
import { reportMutationError } from './api/mutation-errors'
import { onWsEvent } from './stores/websocket'
import { refreshOnRecordingDraftDiscarded } from './lib/recording-draft-discarded'
import './index.css'

initSentry()

// A deploy replaces every hashed chunk under /assets. A tab opened before it
// still holds the old index.html, and its next lazy route import 404s (the
// server stopped answering those with index.html — CLA-1255). Vite raises
// this event for exactly that case; one reload fetches the new index.html.
// The sessionStorage stamp stops a genuinely missing chunk from reload-looping:
// a second failure within a minute of the last reload is left to surface.
window.addEventListener('vite:preloadError', (event) => {
  const key = 'claro:preload-reload-at'
  const now = Date.now()
  try {
    const last = Number(sessionStorage.getItem(key) ?? 0)
    if (now - last < 60_000) return
    sessionStorage.setItem(key, String(now))
  } catch {
    return // no storage means no loop guard — let the error surface instead
  }
  event.preventDefault()
  location.reload()
})

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (err, query) => {
      // Queries swallow their fetcher's rejection internally, so failures never
      // reach Sentry's global handlers. Surface real faults explicitly (CLA-694).
      captureHandledError(err, {
        source: 'react-query',
        kind: 'query',
        resource: String(query.queryKey?.[0] ?? 'unknown'),
      })
    },
  }),
  mutationCache: new MutationCache({
    onError: (err, _vars, _ctx, mutation) => reportMutationError(err, mutation),
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

// After a WebSocket outage, every broadcast in the gap is gone — WS-driven
// caches (notes lists, open notes) may be arbitrarily stale. Refetch all
// active queries on reconnect; quiet reconnect blips don't emit this event.
onWsEvent((event) => {
  if ((event as { type?: string }).type === 'ws.reconnected') {
    queryClient.invalidateQueries()
  }
})

// A recording thrown away mid-consult discards its server draft (CLA-1875),
// and that broadcasts nothing; refresh the lists that showed it as live.
refreshOnRecordingDraftDiscarded(queryClient)

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
)
